import {
  AngleRight,
  ChakraBadge,
  ChakraBox,
  ChakraButton,
  ChakraFlex,
  ChakraSpacer,
  ChakraText,
  Counter,
  EquipUIFireflyV2Theme,
  Text,
} from '@equip.health/ui';
import { useHistory } from 'react-router-dom';

import { getOverviewPageTitle } from '~/analytics/overviewPageEvents';
import { AnalyticsSourceValues } from '~/lib/constants/AnalyticsSource.constants';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import { Path } from '~/routes/constants';

export type SectionHeaderBadge =
  | {
      type: 'pill';
      count: number;
      text: string;
      color?: string;
    }
  | {
      type: 'counter';
      count: number;
      counterId?: string;
      description?: string;
    };

export interface SectionHeaderAction {
  /** Overrides the button's accessible name. Set it where sibling sections share one visible label. */
  ariaLabel?: string;
  linkTo: string;
  patientId: string;
  rightLinkText: string;
}

interface SectionHeaderProps {
  headerText: string;
  variant?: 'h5' | 'bodyMedium' | 'caption';
  headerTextColor?: string;
  badge?: SectionHeaderBadge;
  action?: SectionHeaderAction;
  id?: string;
}

const SectionHeader = ({
  action,
  badge,
  headerText,
  variant = 'h5',
  headerTextColor,
  id,
}: SectionHeaderProps) => {
  const { primary } = EquipUIFireflyV2Theme.colors;
  const history = useHistory();
  const { trackEvent } = useAnalytics();

  const handleActionClick = () => {
    if (!action) return;

    const { linkTo, patientId } = action;
    history.push(`${Path.MAUD_PATIENTS}/${patientId}/${linkTo}`);
    trackEvent(getOverviewPageTitle(linkTo), {
      Source: AnalyticsSourceValues.OVERVIEW_TAB,
    });
  };

  const renderBadge = () => {
    if (!badge) return null;

    if (badge.type === 'pill') {
      const { color, count, text } = badge;
      if (!count) return null;

      return (
        <ChakraBadge
          backgroundColor={color}
          borderRadius="100px"
          color="white"
          height="25px"
          marginLeft="10px"
          padding="3px 8px"
          textTransform="none"
        >
          <ChakraText>{`${count} ${text}`}</ChakraText>
        </ChakraBadge>
      );
    }

    const { count, counterId, description } = badge;
    if (count <= 0) return null;

    return (
      <ChakraBox marginLeft="10px">
        <Counter description={description} id={counterId} number={count} />
      </ChakraBox>
    );
  };

  const renderHeaderText = () => {
    return (
      <Text color={headerTextColor} id={id} variant={variant}>
        {headerText}
      </Text>
    );
  };

  return (
    <ChakraFlex alignItems="center">
      {renderHeaderText()}
      {renderBadge()}
      <ChakraSpacer />
      {action ? (
        <ChakraButton
          _active={{
            backgroundColor: 'transparent',
            boxShadow: 'none',
          }}
          _hover={{ background: 'transparent' }}
          aria-label={action.ariaLabel}
          background="transparent"
          color="primary.100"
          fontSize="12px"
          fontWeight="700"
          onClick={handleActionClick}
          rightIcon={
            <AngleRight fill={primary[100]} height="10px" width="15px" />
          }
          size="sm"
        >
          {action.rightLinkText}
        </ChakraButton>
      ) : null}
    </ChakraFlex>
  );
};

export default SectionHeader;
